import { Dispatch, useEffect, useReducer } from "react";
import Loading from "components/elements/Loading/Loading";
import AutodeskLogoSvg from "components/icons/autodesk-logo.svg";
import { useAutodeskUserAccounts } from "lib/hooks/autodesk/useAutodeskUserAccounts";
import {
  AutodeskImportAction,
  AutodeskImportState,
  autodeskImportReducer,
  initialAutodeskImportState,
} from "./autodeskImportReducer";
import AutodeskBreadcrumbs, {
  AutodeskCrumb,
} from "./controls/AutodeskBreadcrumbs";
import AutodeskRequestError from "./controls/AutodeskRequestError";
import AutodeskImportConnectStep from "./steps/AutodeskImportConnectStep";
import AutodeskImportAccountStep from "./steps/AutodeskImportAccountStep";
import AutodeskImportHubStep from "./steps/AutodeskImportHubStep";
import AutodeskImportProjectStep from "./steps/AutodeskImportProjectStep";
import AutodeskImportFolderStep from "./steps/AutodeskImportFolderStep";
import AutodeskImportReviewStep from "./steps/AutodeskImportReviewStep";
import AutodeskImportResultsStep from "./steps/AutodeskImportResultsStep";

export default function AutodeskImportBrowser(p: {
  projectId: number;
  workspaceId: number;
  onClose(): void;
}) {
  const [state, dispatch] = useReducer(
    autodeskImportReducer,
    initialAutodeskImportState,
  );
  const { accounts, isLoading, hasError, mutate } = useAutodeskUserAccounts(
    p.workspaceId,
  );
  const onlyAccountId = accounts.length === 1 ? accounts[0].id : null;
  const hasSeveralAccounts = accounts.length > 1;

  useEffect(() => {
    if (state.step !== "account" || onlyAccountId === null) return;
    dispatch({ type: "CHOOSE_ACCOUNT", accountId: onlyAccountId });
  }, [state.step, onlyAccountId]);

  // Reconnecting can change which accounts the user has, so start again from
  // the account rather than keep one that may be gone.
  async function onReconnected() {
    await mutate();
    dispatch({ type: "RESET" });
  }

  const crumbs = getCrumbs(state, dispatch, hasSeveralAccounts);

  return (
    <div className="flex h-[40rem] flex-col gap-6">
      <header className="flex flex-col items-start gap-1 font-medium">
        <AutodeskLogoSvg className="w-28" />
        Autodesk Forma
      </header>
      {crumbs.length > 0 && <AutodeskBreadcrumbs crumbs={crumbs} />}
      <div className="flex-1 overflow-y-auto pb-4 pr-2">{renderStepBody()}</div>
    </div>
  );

  function renderStepBody() {
    if (isLoading) return <Loading />;
    if (hasError)
      return (
        <AutodeskRequestError
          error={null}
          workspaceId={p.workspaceId}
          onRetry={() => mutate()}
          onReconnected={onReconnected}
        />
      );
    if (accounts.length === 0)
      return (
        <AutodeskImportConnectStep
          workspaceId={p.workspaceId}
          onConnected={mutate}
        />
      );

    const stepProps = {
      state,
      dispatch,
      workspaceId: p.workspaceId,
      onReconnected,
    };

    switch (state.step) {
      case "account":
        return hasSeveralAccounts ? (
          <AutodeskImportAccountStep accounts={accounts} dispatch={dispatch} />
        ) : (
          <Loading />
        );
      case "hub":
        return <AutodeskImportHubStep {...stepProps} />;
      case "project":
        return <AutodeskImportProjectStep {...stepProps} />;
      case "folder":
        return (
          <AutodeskImportFolderStep
            key={state.folderPath.at(-1)?.id ?? "top"}
            {...stepProps}
          />
        );
      case "review":
        return (
          <AutodeskImportReviewStep
            {...stepProps}
            projectId={p.projectId}
            onClose={p.onClose}
          />
        );
      case "results":
        return <AutodeskImportResultsStep state={state} onClose={p.onClose} />;
    }
  }
}

function getCrumbs(
  state: AutodeskImportState,
  dispatch: Dispatch<AutodeskImportAction>,
  hasSeveralAccounts: boolean,
): AutodeskCrumb[] {
  if (state.step === "account") return [];

  const crumbs: AutodeskCrumb[] = [];
  if (hasSeveralAccounts)
    crumbs.push({
      label: "Accounts",
      onClick: () => dispatch({ type: "RESET" }),
    });
  crumbs.push({
    label: "Hubs",
    onClick: () => dispatch({ type: "SHOW_HUBS" }),
  });
  if (state.hub)
    crumbs.push({
      label: state.hub.name,
      onClick: () => dispatch({ type: "SHOW_PROJECTS" }),
    });
  if (state.project)
    crumbs.push({
      label: state.project.name,
      onClick: () => dispatch({ type: "SHOW_FOLDER", depth: 0 }),
    });
  state.folderPath.forEach((folder, index) =>
    crumbs.push({
      label: folder.name,
      onClick: () => dispatch({ type: "SHOW_FOLDER", depth: index + 1 }),
    }),
  );
  if (state.step === "review" || state.step === "results")
    crumbs.push({ label: "Review" });
  // Leaving mid-import would drop the per-file results of imports already started.
  if (state.isImporting) return crumbs.map((crumb) => ({ label: crumb.label }));
  return crumbs;
}
